import { useRef } from 'react';
import { ENTITY_TYPE } from './bi-fields';
import { useItemSeenBi } from './use-item-seen-bi';

// Pieces already counted, per conversation. Module state, because a docked card unmounts and mounts again as the
// owner leaves the chat and comes back, and the transcript redraws every answer card: that is the same piece
// shown once, not again. Same rule as `useSurveyShownBi`, and as the phones' `ValueFirstBI.seen`.
const reported = new Map<string, Set<string>>();

export function resetValueFirstSeenBi() {
  reported.clear();
}

/**
 * The `itemSeen` for one value-first piece (lib/value-first-bi), counted once per conversation and `key`: the
 * message id for an answer card, the component for a docked question. Returns the ref to put on the piece.
 * `enabled: false` while the chat surface is closed: the floating chat stays laid out behind `visibility: hidden`,
 * which an IntersectionObserver does not see.
 */
export function useValueFirstSeenBi(
  component: string,
  conversationId: string | null | undefined,
  key: string | undefined,
  fields: Record<string, string | undefined>,
  enabled = true,
): (node: Element | null) => void {
  const thread = enabled ? conversationId ?? '' : '';
  const id = `${component}|${key ?? ''}`;
  // Claimed on render rather than in an effect, because `useItemSeenBi` owns the observer and fires by itself.
  const decided = useRef<{ id: string; report: boolean } | undefined>(undefined);
  if (thread && decided.current?.id !== `${thread}|${id}`) {
    let seen = reported.get(thread);
    if (!seen) {
      seen = new Set();
      reported.set(thread, seen);
    }
    const fresh = !seen.has(id);
    if (fresh) seen.add(id);
    decided.current = { id: `${thread}|${id}`, report: fresh };
  }
  return useItemSeenBi(component, {
    ...(thread ? { entity: { id: thread, type: ENTITY_TYPE.conversation } } : {}),
    additionalFields: { ...fields, conversation_id: thread || undefined },
    disabled: !thread || !decided.current?.report,
  });
}
